import { useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePersonStore } from 'stores';
import { applyLibraryFilters } from 'utils/libraryFilters';

export function useLibraryNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const personStore = usePersonStore();
  const { activities, keyword, libraryViewMode, setLibraryViewMode, setLibrarySection } =
    personStore;

  const personName = params.person_name ? decodeURIComponent(params.person_name) : null;
  const boardName = params.keyword ? decodeURIComponent(params.keyword) : null;

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchQuery = searchParams.get('q') || '';
  const isCanvasRoute = Boolean(boardName) && searchParams.get('view') !== 'gallery';

  const activeSection = useMemo(() => {
    if (searchParams.get('create') === '1') return 'create';
    if (isCanvasRoute && boardName) return 'board';
    if (boardName) return 'board';
    if (searchQuery) return 'search';
    return 'all';
  }, [isCanvasRoute, boardName, searchQuery, searchParams]);

  const filteredActivities = useMemo(
    () =>
      applyLibraryFilters(activities, {
        section: activeSection,
        // Board route already loads scoped activities via loadCanvas
        boardName: keyword ? null : boardName || null,
        searchQuery,
      }),
    [activities, activeSection, boardName, keyword, searchQuery]
  );

  const buildPath = useCallback(
    (opts = {}) => {
      const { board, q, canvas } = opts;
      let path = `/${encodeURIComponent(personName)}`;
      if (board) {
        path += `/${encodeURIComponent(board)}`;
      }
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (board && canvas === false) qs.set('view', 'gallery');
      const query = qs.toString();
      return query ? `${path}?${query}` : path;
    },
    [personName]
  );

  const navigateAll = useCallback(() => {
    setLibrarySection('all');
    navigate(buildPath({ section: 'all' }));
  }, [navigate, buildPath, setLibrarySection]);

  const navigateBoard = useCallback(
    (name, { canvas = true } = {}) => {
      setLibrarySection('board');
      // Boards always open on the canvas; pin grid is opt-in via the view toggle.
      if (canvas) setLibraryViewMode('canvas');
      navigate(buildPath({ board: name, canvas }));
    },
    [navigate, buildPath, setLibrarySection, setLibraryViewMode]
  );

  const navigateSearch = useCallback(
    q => {
      setLibrarySection('search');
      const trimmed = String(q || '').trim();
      navigate(buildPath({ q: trimmed || undefined }));
    },
    [navigate, buildPath, setLibrarySection]
  );

  const navigateCreate = useCallback(() => {
    if (!personName) return;
    setLibrarySection('create');
    navigate(`/${encodeURIComponent(personName)}?create=1`);
  }, [navigate, personName, setLibrarySection]);

  const setViewMode = useCallback(
    mode => {
      setLibraryViewMode(mode);
      if (mode === 'canvas' && boardName) {
        navigate(buildPath({ board: boardName, canvas: true, q: searchQuery || undefined }));
      } else if (mode !== 'canvas' && isCanvasRoute && boardName) {
        navigate(buildPath({ board: boardName, canvas: false, q: searchQuery || undefined }));
      }
    },
    [setLibraryViewMode, boardName, isCanvasRoute, navigate, buildPath, searchQuery]
  );

  const sectionTitle = useMemo(() => {
    if (activeSection === 'create') return 'New board';
    if (isCanvasRoute && boardName) return boardName;
    if (boardName) return boardName;
    if (searchQuery) return `Search: "${searchQuery}"`;
    return 'All items';
  }, [isCanvasRoute, boardName, searchQuery, activeSection]);

  return {
    personName,
    boardName,
    keyword,
    isCanvasRoute,
    activeSection,
    searchQuery,
    filteredActivities,
    libraryViewMode: isCanvasRoute
      ? 'canvas'
      : libraryViewMode === 'list'
        ? 'gallery'
        : libraryViewMode,
    sectionTitle,
    navigateAll,
    navigateBoard,
    navigateSearch,
    navigateCreate,
    setViewMode,
    buildPath,
  };
}
